import { Box } from "@mui/material"
import { AddOutlined } from "@mui-symbols-material/w300"
import { useSearch } from "@tanstack/react-router"
import { type ReactElement, useEffect } from "react"
import { twMerge } from "tailwind-merge"

import { useContactSubscriptionsStore } from "../stores/useContactSubscriptionsStore"

import { CompanySearchFilterChips } from "./filters/company/CompanySearchFilterChips"
import { LocationSearchFilterChips } from "./filters/location/LocationSearchFilterChip"

import { Button } from "@/components/common/Button"
import CompaniesSearchBar from "@/screens/Companies/components/CompaniesSearchBar"
import { CompanyTypeButtonGroup } from "@/screens/Companies/components/CompanyTypeButtonGroup"
import { ContactSearchFilterChips } from "@/screens/Companies/components/filters/contact/ContactSearchFilterChip"
import CertificationLegend from "@/screens/Companies/edit/components/certification/components/CertificationLegend"
import { useCompanyTypesStore } from "@/screens/Companies/stores/useCompanyTypesStore"
import { SearchCategory } from "@/screens/Companies/types/enums"

export const CompaniesSearch = ({
  onShowModal,
  onShowFilter,
}: {
  onShowModal: () => void
  onShowFilter: () => void
}): ReactElement => {
  const { category } = useSearch({
    from: "/app/companies/",
    strict: true,
  })

  const companyTypes = useCompanyTypesStore((state) => state.companyTypes)
  const companyTypeFetching = useCompanyTypesStore((state) => state.fetching)
  const fetchCompanyTypes = useCompanyTypesStore((state) => state.fetchCompanyTypes)

  const contactSubscriptions = useContactSubscriptionsStore((state) => state.contactSubscriptions)
  const contactSubscriptionFetching = useContactSubscriptionsStore((state) => state.fetching)
  const fetchContactSubscriptions = useContactSubscriptionsStore((state) => state.fetchContactSubscriptions)

  useEffect(() => {
    if (!companyTypeFetching && companyTypes.length === 0) {
      fetchCompanyTypes()
    }
    if (!contactSubscriptionFetching && contactSubscriptions.length === 0) {
      fetchContactSubscriptions()
    }
  }, [
    companyTypeFetching,
    companyTypes.length,
    contactSubscriptionFetching,
    contactSubscriptions.length,
    fetchCompanyTypes,
    fetchContactSubscriptions,
  ])

  return (
    <>
      <Box data-testid='companies-search' className={twMerge("flex items-center", "justify-between")}>
        <CompanyTypeButtonGroup companyTypes={companyTypes} fetching={companyTypeFetching} />

        <Button startIcon={<AddOutlined />} appearance='outlined' onClick={onShowModal}>
          New Company
        </Button>
      </Box>
      <Box className='flex justify-between py-6'>
        <CompaniesSearchBar onShowFilter={onShowFilter} displayFilter={category !== SearchCategory.CERTIFICATION} />
        {category && category === SearchCategory.CERTIFICATION && <CertificationLegend />}
      </Box>
      <Box data-testid='companies-search-filter-chips' className='flex flex-wrap items-center gap-4 pb-6'>
        {category && category === SearchCategory.COMPANY && <CompanySearchFilterChips companyTypes={companyTypes} />}
        {category && category === SearchCategory.CONTACT && <ContactSearchFilterChips companyTypes={companyTypes} />}
        {category && category === SearchCategory.LOCATION && <LocationSearchFilterChips companyTypes={companyTypes} />}
      </Box>
    </>
  )
}
